import React, { useCallback, useState, useEffect } from "react";
import ReactDataGrid from "react-data-grid";

import { useField } from "react-final-form";
import { findIndex, isNumber } from "lodash";
import { useNotify } from "react-admin";

interface IAreaDatagrid {
  edit?: boolean;
  ccaAreas: IArea[];
  isDisabled?: boolean;
}

interface IRow {
  id?: number;
  ccaAreaId: number;
  area: string;
  price: number;
}

interface IRateChartInsert {
  id?: number;
  ccaAreaId: number;
  price: number;
}

const PriceFormatter = ({ value }: any) => {
  return (
    <span>
      {Number(Math.round((Number(value) + Number.EPSILON) * 100) / 100)}
    </span>
  );
};

const columns = [
  { key: "area", name: "Area" },
  {
    key: "price",
    name: "Price",
    editable: true,
    formatter: PriceFormatter,
  },
];

const CcaRateChartAreaDatagrid: React.FC<IAreaDatagrid> = ({
  ccaAreas,
  edit,
  isDisabled,
}) => {
  const [rows, setRows] = useState<IRow[] | []>([]);
  const [isLoaded, setLoaded] = useState(false);
  const notify = useNotify();

  //only used for update case
  const rateChartItems = useField("ccaRateChartAreaItems");
  const itemsToSend = useField("itemsToSend");

  //populate rows data with updated areas data from parent component
  useEffect(() => {
    let rowData: IRow[] = [];

    if (!edit) {
      //prefill empty placeholder for create-case
      ccaAreas.forEach(({ name, id }: any) => {
        const data: IRow = { ccaAreaId: id, area: name, price: 0 };
        rowData.push(data);
      });
    }

    setRows(rowData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ccaAreas]);

  // ratechart items values only for first time if it is not undefined
  useEffect(() => {
    if (ccaAreas.length !== 0) {
      if (!isLoaded && rateChartItems.input.value !== "") {
        let rowData: IRow[] = [];

        ccaAreas.forEach((area: any) => {
          const data: IRow = {
            ccaAreaId: area.id,
            area: area.name,
            price: 0,
          };
          rowData.push(data);
        });

        rateChartItems.input.value.forEach((item: any) => {
          let area = findIndex(rowData, {
            ccaAreaId: item.ccaAreaId,
          });

          rowData[area].id = item.id || 0;
          rowData[area].price = item.price / 100;
        });

        setRows(rowData);
        setLoaded(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rateChartItems.input.value, ccaAreas]);

  //update react-form data provider if there is a change in rows
  useEffect(() => {
    const insertData: IRateChartInsert[] = [];

    rows.forEach((rowItem: IRow) => {
      insertData.push({
        id: rowItem.id || 0,
        ccaAreaId: rowItem.ccaAreaId,
        price: Number(rowItem.price) * 100,
      });
    });

    itemsToSend.input.onChange(insertData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const onGridRowsUpdated = useCallback(
    ({ fromRow, toRow, updated }: any) => {
      const updatedRows = rows.slice();

      if (updated.hasOwnProperty("price") && !isNumber(Number(updated.price))) {
        return notify(`Please input Number`, "warning");
      }

      for (let i = fromRow; i <= toRow; i++) {
        updatedRows[i] = { ...updatedRows[i], ...updated };
      }

      setRows(updatedRows);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setRows, rows]
  );

  if (ccaAreas.length === 0) {
    return <h4>Areas data is not set.</h4>;
  }

  if (rows.length === 0) {
    return <h4>Loading...</h4>;
  } else {
    return (
      <ReactDataGrid
        columns={columns}
        rowGetter={(i) => rows[i]}
        rowsCount={rows.length}
        minHeight={400}
        enableCellSelect={isDisabled ? false : true}
        onGridRowsUpdated={onGridRowsUpdated}
      />
    );
  }
};

export default CcaRateChartAreaDatagrid;
